import React, { useEffect, useState } from 'react';
import { searchEntries } from '../../api/metaRegistriesApi';
import type { MetaRegistryEntry } from '../../types/systems';

interface Props {
  open: boolean;
  mapEntryId: string;
  existingBcIds: Set<string>;
  parentBcEntryId: string | null;
  onConfirm: (entry: MetaRegistryEntry) => void;
  onClose: () => void;
}

export const BcmAddNodeModal: React.FC<Props> = ({
  open,
  existingBcIds,
  parentBcEntryId,
  onConfirm,
  onClose,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MetaRegistryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setValidationError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || query.trim().length < 1) {
      setResults([]);
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const found = await searchEntries({
          q: query.trim(),
          slugs: ['business-capabilities'],
          limit: 20,
        });
        setResults(found);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => window.clearTimeout(timer);
  }, [open, query]);

  const handleSelect = (entry: MetaRegistryEntry) => {
    if (parentBcEntryId && entry.parent_id !== parentBcEntryId) {
      setValidationError(
        `Запись "${entry.name}" принадлежит другому родителю в реестре. Разместите её в правильный блок.`,
      );
      return;
    }
    setValidationError(null);
    onConfirm(entry);
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: '#1e293b',
          borderRadius: 8,
          padding: 24,
          width: 480,
          color: '#e2e8f0',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}
      >
        <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Добавить бизнес-способность</h3>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск по названию или коду..."
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid #334155',
            background: '#0f172a',
            color: '#e2e8f0',
            fontSize: 14,
            boxSizing: 'border-box',
          }}
        />
        {validationError && (
          <p style={{ color: '#f87171', fontSize: 13, margin: '8px 0 0' }}>{validationError}</p>
        )}
        <div style={{ marginTop: 12, maxHeight: 300, overflowY: 'auto' }}>
          {loading && <p style={{ color: '#94a3b8', fontSize: 13, padding: '8px 0' }}>Поиск…</p>}
          {!loading && results.length === 0 && query.length > 0 && (
            <p style={{ color: '#64748b', fontSize: 13, padding: '8px 0' }}>Ничего не найдено</p>
          )}
          {results.map((entry) => {
            const onMap = existingBcIds.has(entry.id);
            return (
              <button
                key={entry.id}
                onClick={() => handleSelect(entry)}
                disabled={onMap}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '8px 12px',
                  marginBottom: 4,
                  background: onMap ? '#0f172a' : '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: 6,
                  color: onMap ? '#64748b' : '#e2e8f0',
                  cursor: onMap ? 'not-allowed' : 'pointer',
                  textAlign: 'left',
                  opacity: onMap ? 0.6 : 1,
                }}
              >
                <span style={{ fontSize: 14 }}>{entry.name}</span>
                {onMap && (
                  <span
                    style={{
                      fontSize: 11,
                      color: '#38bdf8',
                      background: '#0c4a6e',
                      padding: '2px 8px',
                      borderRadius: 4,
                    }}
                  >
                    на карте
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              padding: '6px 16px',
              borderRadius: 6,
              border: '1px solid #334155',
              background: 'transparent',
              color: '#94a3b8',
              cursor: 'pointer',
            }}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
};
